import React, { useCallback, useEffect, useRef, useState } from 'react';
import { usePrimeModel } from '../../contexts/PrimeModelContext';
import { useTreeState } from '../../contexts/TreeStateContext';
import { PrimeArtifact, UpdatePrimeArtifact } from '../../types/primeArtifact';
import {
  PrimeArtifactState,
  PrimeArtifactStateTransition,
  PrimeProcess,
} from '../../types/primeModel';
import { usePrimeArtifact, useFlatArtifacts } from '../../store/hooks/usePrimeArtifact';
import { useSelector, useDispatch } from 'react-redux';
import { RootState, AppDispatch } from '../../store';
import { fetchFlatArtifacts, updatePrimeArtifact } from '../../store/actions/primeArtifactActions';
import { GPTService } from '../../services/gptService';
import PrimeStateForm from './PrimeStateForm';
import PrimeTransitionForm from './PrimeTransitionForm';
import { PrimeProcessForm } from './PrimeProcessForm';
import styles from './PrimeArtifactDetails.module.css';
import { toast } from 'react-hot-toast';
import { PrimeArtifactService } from '../../services/primeArtifactService';
import {
  fetchPrimeArtifactStates,
  fetchPrimeArtifactTransitions,
  fetchArtifactTypes,
} from '../../store/actions/primeArtifactActions';
import { selectPrimeArtifacts } from '../../store/selectors/primeArtifactSelectors';

// Add this helper function before the component

// Add GPT response types
interface GPTResponse {
  id: string;
  prompt: string;
  raw_response: string;
  response_type: string;
  is_approved: boolean;
  is_processed: boolean;
  created_at: string;
}

interface ModelData {
  states?: Array<{
    name: string;
    description: string;
    is_initial: boolean;
    is_final: boolean;
  }>;
  transitions?: Array<{
    name: string;
    description: string;
    from_state: string;
    to_state: string;
  }>;
  processes?: Array<{
    name: string;
    description: string;
    type: string;
  }>;
}

// Add these interfaces after the GPTResponse interface
interface GPTResponseComponent {
  type: 'state' | 'transition' | 'process';
  data: Record<string, unknown>;
}

interface Message {
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
}

interface GPTChatProps {
  onArtifactGenerated?: (artifact: PrimeArtifact) => void;
  onStatesGenerated?: (states: PrimeArtifactState[]) => void;
  onTransitionsGenerated?: (transitions: PrimeArtifactStateTransition[]) => void;
  onProcessesGenerated?: (processes: PrimeProcess[]) => void;
  onCompleteModelGenerated?: (model: Record<string, unknown>) => void;
  prime_artifact_id?: string;
  parentId?: string;
  artifactTypeId?: string;
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const GPTChat: React.FC<GPTChatProps> = ({
  onArtifactGenerated,
  onStatesGenerated,
  onTransitionsGenerated,
  onProcessesGenerated,
  onCompleteModelGenerated,
  prime_artifact_id,
}) => {
  const [, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [, setIsLoading] = useState(false);
  const [, setError] = useState<string | null>(null);
  const [generationType] = useState<
    'artifact' | 'states' | 'transitions' | 'processes' | 'complete-model'
  >('complete-model');

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleSendMessage = async () => {
    if (!input.trim()) return;

    const userMessage: Message = {
      role: 'user',
      content: input,
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setInput('');
    setIsLoading(true);
    setError(null);

    try {
      let response;

      switch (generationType) {
        case 'artifact':
          response = await GPTService.getInstance().generateArtifact(input);
          if (onArtifactGenerated && response) {
            onArtifactGenerated(response);
          }
          break;
        case 'states':
          if (!prime_artifact_id) {
            throw new Error('Prime Artifact ID is required for generating states');
          }
          response = await GPTService.getInstance().generateStates(prime_artifact_id, input);
          if (onStatesGenerated && response) {
            onStatesGenerated(response as PrimeArtifactState[]);
          }
          break;
        case 'transitions':
          if (!prime_artifact_id) {
            throw new Error('Prime Artifact ID is required for generating transitions');
          }
          response = await GPTService.getInstance().generateTransitions(prime_artifact_id, input);
          if (onTransitionsGenerated && response) {
            onTransitionsGenerated(response as PrimeArtifactStateTransition[]);
          }
          break;
        case 'processes':
          if (!prime_artifact_id) {
            throw new Error('Prime Artifact ID is required for generating processes');
          }
          response = await GPTService.getInstance().generateProcesses(prime_artifact_id, input);
          if (onProcessesGenerated && response) {
            onProcessesGenerated(response as PrimeProcess[]);
          }
          break;
        case 'complete-model':
          response = await GPTService.getInstance().generateCompleteModel(input);
          if (onCompleteModelGenerated && response) {
            onCompleteModelGenerated(response as Record<string, unknown>);
          }
          break;
      }

      const assistantMessage: Message = {
        role: 'assistant',
        content: (response as { message?: string }).message || 'Generation completed successfully.',
        timestamp: new Date(),
      };

      setMessages((prev) => [...prev, assistantMessage]);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
      console.error('Error in GPT chat:', err);
    } finally {
      setIsLoading(false);
    }
  };

  return <div className={styles.gptChatContainer}>{/* Add your chat UI components here */}</div>;
};

const PrimeArtifactDetails: React.FC = React.memo(() => {
  const { selectedArtifact, selectArtifact, saveSelectedArtifact, restoreSelectedArtifact } =
    usePrimeModel();
  const { saveTreeState, restoreTreeState } = useTreeState();
  const dispatch = useDispatch<AppDispatch>();
  const { fetchArtifacts } = usePrimeArtifact(selectedArtifact?.id);
  const flatArtifacts = useFlatArtifacts();
  const primeArtifacts = useSelector(selectPrimeArtifacts);
  const [isEditing, setIsEditing] = useState(false);
  const [editedArtifact, setEditedArtifact] = useState<PrimeArtifact | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingParents, setIsLoadingParents] = useState(false);
  const isSaving = useRef(false);

  // GPT Integration states
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [isGeneratingModel, setIsGeneratingModel] = useState(false);
  const [generationStatus, setGenerationStatus] = useState<string | null>(null);
  const [gptResponses, setGptResponses] = useState<GPTResponse[]>([]);
  const [selectedResponse, setSelectedResponse] = useState<GPTResponse | null>(null);
  const [isProcessingResponse, setIsProcessingResponse] = useState(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [isLoadingResponse, setIsLoadingResponse] = useState(false);

  // Add these states after the existing state declarations
  const [selectedComponent, setSelectedComponent] = useState<GPTResponseComponent | null>(null);
  const [showComponentForm, setShowComponentForm] = useState(false);

  // Get artifact types from Redux state for the dropdown
  const artifactTypes = useSelector((state: RootState) => state.primeArtifacts.artifactTypes);

  // Fetch data when component mounts or selectedArtifact changes
  useEffect(() => {
    const fetchData = async () => {
      if (!selectedArtifact?.id) return;

      try {
        // Fetch all required data in parallel
        await Promise.all([
          PrimeArtifactService.getPrimeArtifactStates(selectedArtifact.id),
          PrimeArtifactService.getPrimeArtifactTransitions(selectedArtifact.id),
          PrimeArtifactService.getPrimeArtifactProcesses(selectedArtifact.id),
          PrimeArtifactService.getAll(),
        ]);

        // Update Redux state
        void dispatch(fetchPrimeArtifactStates(selectedArtifact.id));
        void dispatch(fetchPrimeArtifactTransitions(selectedArtifact.id));
        void dispatch(fetchArtifactTypes()); // This will fetch artifact types via Redux
        void dispatch(fetchFlatArtifacts());

        // Set local state
        setEditedArtifact(selectedArtifact);
      } catch (error) {
        console.error('Error fetching data:', error);
        toast.error('Failed to load artifact data');
      }
    };

    void fetchData();
  }, [selectedArtifact, dispatch]);

  // Add a separate effect to fetch parent artifacts when entering edit mode
  useEffect(() => {
    const loadParentArtifacts = async () => {
      if (isEditing || !flatArtifacts.length) {
        console.log('[PrimeArtifactDetails] Fetching parent artifacts');
        setIsLoadingParents(true);
        try {
          await dispatch(fetchFlatArtifacts());
          console.log('[PrimeArtifactDetails] Parent artifacts fetched successfully');
        } catch (error) {
          console.error('[PrimeArtifactDetails] Error fetching parent artifacts:', error);
        } finally {
          setIsLoadingParents(false);
        }
      }
    };
    void loadParentArtifacts();
  }, [isEditing, dispatch, flatArtifacts.length, selectedArtifact?.id]);

  useEffect(() => {
    console.log('[PrimeArtifactDetails] selectedArtifact changed:', {
      id: selectedArtifact?.id,
      name: selectedArtifact?.name,
      artifactType: selectedArtifact?.artifact_type_name,
      parent: selectedArtifact?.parent_name,
      artifactTypeId: selectedArtifact?.artifact_type_id,
      parentRef: selectedArtifact?.parent_ref,
    });
    if (selectedArtifact) {
      const updateArtifact: UpdatePrimeArtifact = {
        id: selectedArtifact.id,
        name: selectedArtifact.name,
        description: selectedArtifact.description || undefined,
        artifact_type_id: selectedArtifact.artifact_type_id,
        artifact_type_name: selectedArtifact.artifact_type_name || undefined,
        parent_ref: selectedArtifact.parent_ref || undefined,
        parent_name: selectedArtifact.parent_name || undefined,
        states: selectedArtifact.states || [],
        transitions: selectedArtifact.transitions || [],
        processes: selectedArtifact.processes || [],
      };
      console.log('[PrimeArtifactDetails] Setting editedArtifact:', updateArtifact);
      setEditedArtifact(updateArtifact);
    } else {
      console.log('[PrimeArtifactDetails] No selected artifact, clearing editedArtifact');
      setEditedArtifact(null);
    }
  }, [selectedArtifact]);

  // Log when artifact types change
  useEffect(() => {
    console.log('[PrimeArtifactDetails] Artifact types updated:', {
      count: artifactTypes.length,
      types: artifactTypes.map((type) => ({ id: type.id, name: type.name })),
    });
  }, [artifactTypes]);

  // Log when parent artifacts change
  useEffect(() => {
    console.log('[PrimeArtifactDetails] Parent artifacts updated:', {
      count: flatArtifacts.length,
      artifacts: flatArtifacts.map((a) => ({ id: a.id, name: a.name })),
      sorted: [...flatArtifacts]
        .sort((a, b) => a.name.localeCompare(b.name))
        .map((a) => ({ id: a.id, name: a.name })),
    });
  }, [flatArtifacts]);

  // Log when edited artifact changes
  useEffect(() => {
    console.log('[PrimeArtifactDetails] Edited artifact updated:', editedArtifact);
  }, [editedArtifact]);

  // Add a check for when the selected artifact is not found in the parent artifacts
  useEffect(() => {
    console.log('[PrimeArtifactDetails] parentArtifacts or editedArtifact changed:', {
      parentArtifactsCount: flatArtifacts.length,
      editedArtifactId: editedArtifact?.id,
      selectedArtifactId: selectedArtifact?.id,
    });

    if (selectedArtifact && flatArtifacts.length > 0) {
      const foundArtifact = flatArtifacts.find((a) => a.id === selectedArtifact.id);
      if (!foundArtifact) {
        console.warn('[PrimeArtifactDetails] Selected artifact not found in parent artifacts:', {
          selectedArtifactId: selectedArtifact.id,
          selectedArtifactName: selectedArtifact.name,
          parentArtifactsCount: flatArtifacts.length,
        });
      }
    }
  }, [flatArtifacts, editedArtifact, selectedArtifact]);

  const handleEdit = useCallback(() => {
    console.log('[PrimeArtifactDetails] Entering edit mode');
    setIsEditing(true);
  }, []);

  const handleCancel = useCallback(() => {
    console.log('[PrimeArtifactDetails] Canceling edit mode');
    setIsEditing(false);
    if (selectedArtifact) {
      // Find parent name if it's not already set
      let parentName: string | null = selectedArtifact.parent_name || null;
      if (!parentName && selectedArtifact.parent_ref && flatArtifacts.length > 0) {
        const parent = flatArtifacts.find((a) => a.id === selectedArtifact.parent_ref);
        parentName = parent?.name || null;
        console.log('[PrimeArtifactDetails] Resetting parent name from artifacts list:', {
          parentRef: selectedArtifact.parent_ref,
          foundParent: parent,
          parentName,
        });
      }

      setEditedArtifact({
        id: selectedArtifact.id,
        name: selectedArtifact.name,
        description: selectedArtifact.description,
        artifact_type_id: selectedArtifact.artifact_type_id,
        artifact_type_name: selectedArtifact.artifact_type_name,
        parent_ref: selectedArtifact.parent_ref,
        parent_name: parentName || undefined,
        states: selectedArtifact.states || [],
        transitions: selectedArtifact.transitions || [],
        processes: selectedArtifact.processes || [],
      });
    }
  }, [selectedArtifact, flatArtifacts]);

  const handleSave = useCallback(async () => {
    if (!editedArtifact || isSaving.current) return;

    try {
      isSaving.current = true;
      console.log('[PrimeArtifactDetails] Saving artifact:', editedArtifact);
      setIsLoading(true);

      // Save the current tree state and selected artifact before updating
      saveTreeState();
      saveSelectedArtifact();

      // Store the current artifact name for restoration
      const currentArtifactName = selectedArtifact?.name;
      console.log(
        '[PrimeArtifactDetails] Storing current artifact name for restoration:',
        currentArtifactName
      );

      await dispatch(updatePrimeArtifact(editedArtifact));
      setIsEditing(false);

      // Refresh the artifact list to get updated data
      await fetchArtifacts();

      // Restore the tree state and selected artifact after updating
      restoreTreeState();
      restoreSelectedArtifact();

      // If the artifact name changed, we need to update the selected artifact
      if (currentArtifactName && editedArtifact.name !== currentArtifactName) {
        console.log('[PrimeArtifactDetails] Artifact name changed, updating selection:', {
          oldName: currentArtifactName,
          newName: editedArtifact.name,
        });

        // Find the updated artifact in the parent artifacts list
        const updatedArtifact = flatArtifacts.find((a) => a.name === editedArtifact.name);
        if (updatedArtifact) {
          console.log(
            '[PrimeArtifactDetails] Found updated artifact, selecting it:',
            updatedArtifact.name
          );
          selectArtifact(updatedArtifact);
        }
      }
    } catch (error) {
      console.error('Error saving artifact:', error);
    } finally {
      setIsLoading(false);
      isSaving.current = false;
    }
  }, [
    editedArtifact,
    dispatch,
    fetchArtifacts,
    saveTreeState,
    saveSelectedArtifact,
    restoreTreeState,
    restoreSelectedArtifact,
    selectedArtifact,
    flatArtifacts,
    selectArtifact,
  ]);

  const handleChange = useCallback(
    (field: keyof UpdatePrimeArtifact, value: string | null) => {
      if (!editedArtifact) return;

      console.log(`[PrimeArtifactDetails] Field changed: ${field}`, { value });

      // For artifact_type_id, also update the artifact_type_name
      if (field === 'artifact_type_id' && value) {
        const selectedType = artifactTypes.find((type) => type.id === value);
        console.log('[PrimeArtifactDetails] Selected artifact type:', selectedType);
        setEditedArtifact((prev) =>
          prev
            ? {
                ...prev,
                [field]: value,
                artifact_type_name: selectedType?.name || undefined,
              }
            : null
        );
        return;
      }

      // For parent_ref, also update the parent_name
      if (field === 'parent_ref' && value) {
        const selectedParent = flatArtifacts.find((artifact) => artifact.id === value);
        console.log('[PrimeArtifactDetails] Selected parent artifact:', selectedParent);
        setEditedArtifact((prev) =>
          prev
            ? {
                ...prev,
                [field]: value,
                parent_name: selectedParent?.name || undefined,
              }
            : null
        );
        return;
      }

      setEditedArtifact((prev) => (prev ? { ...prev, [field]: value } : null));
    },
    [editedArtifact, artifactTypes, flatArtifacts]
  );

  // GPT Integration functions
  const handleGenerateModel = async () => {
    if (!selectedArtifact) return;

    try {
      setIsLoadingResponse(true);
      const prompt = `Generate a complete model for ${selectedArtifact.name} (${selectedArtifact.artifact_type_name})`;

      const response = await GPTService.generateCompleteModel(prompt);

      if (response && (response as { responses?: unknown[] }).responses) {
        const responseData = response as { responses: unknown[] };
        setGptResponses(responseData.responses as GPTResponse[]);
        if (responseData.responses.length > 0) {
          setSelectedResponse(responseData.responses[0] as GPTResponse);
        }
      }
    } catch (error) {
      console.error('Error generating model:', error);
      toast.error('Failed to generate model');
    } finally {
      setIsLoadingResponse(false);
    }
  };

  const handleParentChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const newParentId = event.target.value;
    const newParent = flatArtifacts.find((a) => a.id === newParentId);

    if (editedArtifact) {
      const updatedArtifact: UpdatePrimeArtifact = {
        ...editedArtifact,
        parent_ref: newParentId ? newParentId : undefined,
        parent_name: newParent ? newParent.name : undefined,
      };
      setEditedArtifact(updatedArtifact);
    }
  };

  // Add handler for approving a response
  const handleApproveResponse = useCallback(async () => {
    if (!selectedResponse) return;

    setIsProcessingResponse(true);
    setGenerationStatus('Processing approved response...');

    try {
      // processResponse returns void, so we don't need to check for errors
      await GPTService.processResponse(selectedResponse.id);
      setGptResponses((prev) =>
        prev.map((response) =>
          response.id === selectedResponse.id
            ? { ...response, is_approved: true, is_processed: true }
            : response
        )
      );

      setGenerationStatus('Response processed successfully!');
      toast.success('Response processed successfully');

      // Refresh the artifact data
      await fetchArtifacts();
    } catch (error: unknown) {
      console.error('Error processing response:', error);
      let errorMessage = 'Failed to process response';
      if (error instanceof Error) {
        errorMessage = error.message;
      }
      setGenerationStatus(`Error: ${errorMessage}`);
      toast.error(errorMessage);
    } finally {
      setIsProcessingResponse(false);
    }
  }, [selectedResponse, fetchArtifacts]);

  // Add these functions before the return statement
  const handleDeleteResponse = async (responseId: string) => {
    try {
      // Remove the response from the list directly since there's no delete endpoint
      setGptResponses((prev) => prev.filter((response) => response.id !== responseId));
      if (selectedResponse?.id === responseId) {
        setSelectedResponse(null);
      }
      toast.success('Response deleted successfully');
    } catch (error) {
      console.error('Error deleting response:', error);
      toast.error('Failed to delete response');
    }
  };

  // Add these handlers before the return statement
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleSelectComponent = (
    response: GPTResponse,
    componentType: 'state' | 'transition' | 'process',
    index: number
  ) => {
    try {
      console.log('Selecting component:', { componentType, index });
      const modelData =
        typeof response.raw_response === 'string'
          ? JSON.parse(response.raw_response)
          : response.raw_response;

      let componentData: {
        state_type: string;
        demand_type: string;
        demand_flow_type: string;
        from_state: string;
        to_state: string;
        process_name: string;
        process_type: string;
        process_parameters: Record<string, unknown>;
        demand_calculation_rules: Record<string, unknown>;
        validation_rules: Record<string, unknown>;
        improvement_ref: string | null;
        improvement_parameters: Record<string, unknown>;
        prime_process_ref?: string;
        prime_artifact_ref?: string;
        from_state_name?: string;
        to_state_name?: string;
      };
      switch (componentType) {
        case 'state':
          componentData = modelData.states?.[index];
          // Convert enum values to lowercase
          if (componentData) {
            componentData = {
              ...componentData,
              state_type: componentData.state_type?.toLowerCase(),
              demand_type: componentData.demand_type?.toLowerCase(),
              demand_flow_type: componentData.demand_flow_type?.toLowerCase(),
            };
          }
          break;
        case 'transition':
          componentData = modelData.transitions?.[index];
          // For transitions, we need to ensure we have the full context
          if (componentData && selectedArtifact) {
            // Get all states and processes from the model data
            const allStates = modelData.states || [];
            const allProcesses = modelData.processes || [];

            // Find the input and output states by name
            const inputState = allStates.find(
              (s: unknown) => (s as { name: string }).name === componentData.from_state
            );
            const outputState = allStates.find(
              (s: unknown) => (s as { name: string }).name === componentData.to_state
            );

            // Find the process by name
            const process = allProcesses.find(
              (p: unknown) => (p as { name: string }).name === componentData.process_name
            );

            componentData = {
              ...componentData,
              from_state: inputState?.id || null,
              to_state: outputState?.id || null,
              prime_process_ref: process?.id || null,
              prime_artifact_ref: selectedArtifact.id,
              // Keep the original names for reference
              from_state_name: componentData.from_state,
              to_state_name: componentData.to_state,
              process_name: componentData.process_name,
            };
          }
          break;
        case 'process':
          componentData = modelData.processes?.[index];
          // Convert enum values to uppercase and set prime_artifact_ref
          if (componentData && selectedArtifact) {
            componentData = {
              ...componentData,
              process_type: componentData.process_type?.toUpperCase() || 'DEMAND',
              demand_flow_type: componentData.demand_flow_type?.toUpperCase() || 'DRUM',
              prime_artifact_ref: selectedArtifact.id,
              process_parameters: componentData.process_parameters || {},
              demand_calculation_rules: componentData.demand_calculation_rules || {},
              validation_rules: componentData.validation_rules || {},
              improvement_ref: componentData.improvement_ref || null,
              improvement_parameters: componentData.improvement_parameters || {},
            };
          }
          break;
      }

      console.log('Component data:', componentData);

      if (componentData) {
        setSelectedComponent({
          type: componentType,
          data: componentData,
        });
        setShowComponentForm(true);
        console.log('Form states updated:', {
          showComponentForm: true,
          selectedComponent: { type: componentType, data: componentData },
        });
      }
    } catch (error) {
      console.error('Error parsing component data:', error);
      toast.error('Failed to parse component data');
    }
  };

  const handleComponentFormSubmit = async (data: Record<string, unknown>) => {
    if (!selectedComponent || !selectedArtifact) {
      console.log('Cannot submit: missing required data', {
        selectedComponent,
        selectedArtifact,
      });
      return;
    }

    try {
      console.log('Submitting component form:', {
        type: selectedComponent.type,
        data,
        artifactId: selectedArtifact.id,
      });

      const prompt = typeof data === 'string' ? data : JSON.stringify(data);

      switch (selectedComponent.type) {
        case 'state':
          if (selectedArtifact) {
            await GPTService.generateStates(selectedArtifact.id, prompt);
          }
          break;
        case 'transition':
          if (selectedArtifact) {
            await GPTService.generateTransitions(selectedArtifact.id, prompt);
          }
          break;
        case 'process':
          if (selectedArtifact) {
            await GPTService.generateProcesses(selectedArtifact.id, prompt);
          }
          break;
      }

      console.log('Component form submitted successfully');
      toast.success(`${selectedComponent.type} created successfully`);
      setShowComponentForm(false);
      setSelectedComponent(null);
      await fetchArtifacts();
    } catch (error) {
      console.error(`Error creating ${selectedComponent.type}:`, error);
      toast.error(`Failed to create ${selectedComponent.type}`);
    }
  };

  const handleComponentFormCancel = () => {
    setShowComponentForm(false);
    setSelectedComponent(null);
  };

  if (!selectedArtifact || !editedArtifact) return null;

  // Sort artifact types and parent artifacts alphabetically
  const sortedArtifactTypes = [...artifactTypes].sort((a, b) => a.name.localeCompare(b.name));
  const sortedParentArtifacts = [...flatArtifacts].sort((a, b) => a.name.localeCompare(b.name));

  // Debug logging
  console.log('[PrimeArtifactDetails] Sorting debug:', {
    artifactTypesCount: artifactTypes.length,
    sortedArtifactTypesCount: sortedArtifactTypes.length,
    flatArtifactsCount: flatArtifacts.length,
    sortedParentArtifactsCount: sortedParentArtifacts.length,
    isEditing,
    editedArtifact: editedArtifact ? { id: editedArtifact.id, name: editedArtifact.name } : null,
  });

  const getArtifactTypeName = () => {
    if (isEditing) {
      const selectedType = artifactTypes.find(
        (type) => type.id === editedArtifact.artifact_type_id
      );
      return selectedType?.name || null;
    }
    return selectedArtifact?.artifact_type_name || null;
  };

  // Get the parent name for display
  const getParentName = () => {
    if (isEditing && editedArtifact) {
      return editedArtifact.parent_name || 'None';
    } else if (selectedArtifact) {
      // In view mode, try to find the parent name from both flatArtifacts and primeArtifacts
      let parentName: string | null = selectedArtifact.parent_name || null;
      let parentFromFlat: PrimeArtifact | undefined;
      let parentFromHierarchy: PrimeArtifact | undefined;

      if (!parentName && selectedArtifact.parent_ref) {
        // First check flatArtifacts
        parentFromFlat = flatArtifacts.find((a) => a.id === selectedArtifact.parent_ref);
        if (parentFromFlat) {
          parentName = parentFromFlat.name;
          console.log('[PrimeArtifactDetails] Found parent in flatArtifacts:', {
            parentRef: selectedArtifact.parent_ref,
            parentName,
            parent: parentFromFlat,
          });
        } else {
          // If not found in flatArtifacts, check primeArtifacts
          parentFromHierarchy = primeArtifacts.find((a) => a.id === selectedArtifact.parent_ref);
          if (parentFromHierarchy) {
            parentName = parentFromHierarchy.name;
            console.log('[PrimeArtifactDetails] Found parent in primeArtifacts:', {
              parentRef: selectedArtifact.parent_ref,
              parentName,
              parent: parentFromHierarchy,
            });
          }
        }
      }

      console.log('[PrimeArtifactDetails] Final parent name resolution:', {
        parentName,
        parentRef: selectedArtifact.parent_ref,
        foundInFlat: !!parentFromFlat,
        foundInHierarchy: !!parentFromHierarchy,
      });

      return parentName || 'None';
    }
    return 'None';
  };

  // Log the filtered parent artifacts
  const filteredParentArtifacts = sortedParentArtifacts.filter(
    (artifact: PrimeArtifact) => artifact.id !== editedArtifact.id
  );
  console.log('[PrimeArtifactDetails] Filtered parent artifacts:', {
    count: filteredParentArtifacts.length,
    artifacts: filteredParentArtifacts.map((a) => ({ id: a.id, name: a.name })),
  });

  const StateForm = React.memo(
    ({
      initialData,
      onSubmit,
      onBack,
    }: {
      initialData: Record<string, unknown>;
      onSubmit: (data: Record<string, unknown>) => void;
      onBack: () => void;
    }) => {
      return (
        <PrimeStateForm
          initialData={{
            ...initialData,
            prime_artifact_ref: selectedArtifact?.id || '',
          }}
          onSubmit={(state: PrimeArtifactState) =>
            onSubmit(state as unknown as Record<string, unknown>)
          }
          onBack={onBack}
        />
      );
    }
  );
  StateForm.displayName = 'StateForm';

  return (
    <div className={styles.artifactDetails}>
      <div className={styles.detailsHeader}>
        <h2>Artifact Details</h2>
        <div className={styles.actionButtons}>
          {!isEditing ? (
            <>
              <button onClick={handleEdit}>Edit</button>
              <button
                onClick={handleGenerateModel}
                className={styles.generateModelButton}
                disabled={isGeneratingModel}
              >
                {isGeneratingModel ? 'Generating...' : 'Generate Component Model'}
              </button>
            </>
          ) : (
            <>
              <button onClick={handleSave} className={styles.saveButton} disabled={isLoading}>
                {isLoading ? 'Saving...' : 'Save'}
              </button>
              <button onClick={handleCancel} className={styles.cancelButton} disabled={isLoading}>
                Cancel
              </button>
            </>
          )}
        </div>
      </div>

      {/* GPT Generation Status */}
      {generationStatus && (
        <div className={`${styles.generationStatus} ${generationStatus.includes('Error') ? styles.error : ''}`}>
          {generationStatus}
        </div>
      )}

      <div className={styles.detailsGrid}>
        <div className={styles.detailItem}>
          <label>Name</label>
          {isEditing && editedArtifact ? (
            <input
              type="text"
              value={editedArtifact.name}
              onChange={(e) => handleChange('name', e.target.value)}
            />
          ) : (
            <span>{editedArtifact?.name || 'No name provided'}</span>
          )}
        </div>

        <div className={styles.detailItem}>
          <label>Artifact Type</label>
          {isEditing && editedArtifact ? (
            <select
              value={editedArtifact.artifact_type_id}
              onChange={(e) => handleChange('artifact_type_id', e.target.value)}
            >
              <option value="">Select a type</option>
              {sortedArtifactTypes.map((type) => (
                <option key={type.id} value={type.id}>
                  {type.name}
                </option>
              ))}
            </select>
          ) : (
            <span>{getArtifactTypeName() || 'No type selected'}</span>
          )}
        </div>

        <div className={styles.detailItem}>
          <label>Parent</label>
          {isEditing && editedArtifact ? (
            <select
              value={editedArtifact.parent_ref || ''}
              onChange={handleParentChange}
              disabled={isLoadingParents}
            >
              <option value="">None</option>
              {isLoadingParents ? (
                <option value="" disabled>
                  Loading...
                </option>
              ) : filteredParentArtifacts.length === 0 ? (
                <option value="" disabled>
                  No parent artifacts available
                </option>
              ) : (
                filteredParentArtifacts.map((artifact: PrimeArtifact) => (
                  <option key={artifact.id} value={artifact.id}>
                    {artifact.name}
                  </option>
                ))
              )}
            </select>
          ) : (
            <span>{getParentName() || 'No parent'}</span>
          )}
        </div>

        <div className={`${styles.detailItem} ${styles.description}`}>
          <label>Description</label>
          {isEditing && editedArtifact ? (
            <textarea
              value={editedArtifact.description || ''}
              onChange={(e) => handleChange('description', e.target.value)}
            />
          ) : (
            <span>{editedArtifact?.description || 'No description provided'}</span>
          )}
        </div>
      </div>

      {/* GPT Response Section */}
      {gptResponses.length > 0 && (
        <div className={styles.gptResponseSection}>
          <h3>Generated Models</h3>
          <div className={styles.responseList}>
            {gptResponses.map((response) => {
              let modelData: ModelData = { states: [], transitions: [], processes: [] };
              try {
                modelData =
                  typeof response.raw_response === 'string'
                    ? JSON.parse(response.raw_response)
                    : response.raw_response;
              } catch (error) {
                console.error('Error parsing GPT response:', error);
                return (
                  <div key={response.id} className={`${styles.responseItem} ${styles.error}`}>
                    <div className={styles.responseHeader}>
                      <span className={styles.responseDate}>
                        {new Date(response.created_at).toLocaleString()}
                      </span>
                      <span className={styles.errorMessage}>Invalid response format</span>
                      <div className={styles.responseActions}>
                        <button
                          className={styles.deleteButton}
                          onClick={(e) => {
                            e.stopPropagation();
                            void handleDeleteResponse(response.id);
                          }}
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  </div>
                );
              }

              return (
                <div
                  key={response.id}
                  className={`${styles.responseItem} ${selectedResponse?.id === response.id ? styles.selected : ''}`}
                  onClick={() => setSelectedResponse(response)}
                >
                  <div className={styles.responseHeader}>
                    <span className={styles.responseDate}>
                      {new Date(response.created_at).toLocaleString()}
                    </span>
                    <div className={styles.responseStats}>
                      <span>States: {modelData.states?.length || 0}</span>
                      <span>Transitions: {modelData.transitions?.length || 0}</span>
                      <span>Processes: {modelData.processes?.length || 0}</span>
                    </div>
                    <div className={styles.responseActions}>
                      {!response.is_approved && (
                        <button
                          className={styles.approveButton}
                          onClick={(e) => {
                            e.stopPropagation();
                            void handleApproveResponse();
                          }}
                        >
                          Approve
                        </button>
                      )}
                      <button
                        className={styles.deleteButton}
                        onClick={(e) => {
                          e.stopPropagation();
                          void handleDeleteResponse(response.id);
                        }}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                  {selectedResponse?.id === response.id && (
                    <div className={styles.responseDetails}>
                      <div className={styles.modelComponents}>
                        <div className={styles.componentSection}>
                          <h4>States</h4>
                          <div className={styles.componentList}>
                            {modelData.states?.map((state, index) => (
                              <div
                                key={index}
                                className={styles.componentItem}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleSelectComponent(response, 'state', index);
                                }}
                              >
                                <span className={styles.componentName}>{state.name}</span>
                                <span className={styles.componentType}>
                                  {state.is_initial
                                    ? 'Initial'
                                    : state.is_final
                                      ? 'Final'
                                      : 'Intermediate'}
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                        <div className={styles.componentSection}>
                          <h4>Transitions</h4>
                          <div className={styles.componentList}>
                            {modelData.transitions?.map((transition, index) => (
                              <div
                                key={index}
                                className={styles.componentItem}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleSelectComponent(response, 'transition', index);
                                }}
                              >
                                <span className={styles.componentName}>{transition.name}</span>
                                <span className={styles.componentInfo}>
                                  {transition.from_state} → {transition.to_state}
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                        <div className={styles.componentSection}>
                          <h4>Processes</h4>
                          <div className={styles.componentList}>
                            {modelData.processes?.map((process, index) => (
                              <div
                                key={index}
                                className={styles.componentItem}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleSelectComponent(response, 'process', index);
                                }}
                              >
                                <span className={styles.componentName}>{process.name}</span>
                                <span className={styles.componentType}>{process.type}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          {selectedResponse && !selectedResponse.is_processed && (
            <div className={styles.responseActions}>
              <button onClick={handleApproveResponse} disabled={isProcessingResponse}>
                {isProcessingResponse ? 'Processing...' : 'Approve & Process'}
              </button>
            </div>
          )}
        </div>
      )}

      {showComponentForm && selectedComponent && (
        <div
          className={styles.componentFormModal}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1000,
          }}
        >
          <div
            className={styles.componentFormContent}
            style={{
              backgroundColor: 'white',
              padding: '20px',
              borderRadius: '8px',
              width: '500px',
              maxWidth: '90%',
              boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)',
            }}
          >
            <h3>Create {selectedComponent.type}</h3>
            {selectedComponent.type === 'state' && (
              <StateForm
                initialData={selectedComponent.data}
                onSubmit={handleComponentFormSubmit}
                onBack={handleComponentFormCancel}
              />
            )}
            {selectedComponent.type === 'transition' && (
              <PrimeTransitionForm
                prime_artifact_id={selectedArtifact?.id || ''}
                primeStates={selectedArtifact?.states || []}
                primeProcesses={selectedArtifact?.processes || []}
                initialData={[selectedComponent.data]}
                onSubmit={(data: unknown) =>
                  handleComponentFormSubmit(data as Record<string, unknown>)
                }
                onBack={handleComponentFormCancel}
              />
            )}
            {selectedComponent.type === 'process' && (
              <PrimeProcessForm
                initialData={selectedComponent.data}
                onSubmit={(process: PrimeProcess) =>
                  handleComponentFormSubmit(process as unknown as Record<string, unknown>)
                }
                onCancel={handleComponentFormCancel}
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
});

PrimeArtifactDetails.displayName = 'PrimeArtifactDetails';

export default PrimeArtifactDetails;
